import { parseUtcTimestamp } from "../../../services/dateTime";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import BatchStatusBadge from "../../batches/components/BatchStatusBadge";

/** Current batch with navigation back to its selected analytics filters. */
const relativeUpdatedAt = (value) => {
  if (!value) return "—";
  const date = parseUtcTimestamp(value);
  if (!date) return "—";
  const timestamp = date.getTime();
  const minutes = Math.max(0, Math.round((Date.now() - timestamp) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  return `${hours} hr${hours === 1 ? "" : "s"} ago`;
};

const CurrentBatchCard = ({ batch, isLoading }) => {
  const navigate = useNavigate();
  const location = useLocation();

  if (isLoading) return <div role="status" className="py-5 text-sm text-slate-400">Loading current batch…</div>;

  if (!batch) {
    return (
      <div className="py-5 text-sm text-slate-400">No active batch matches the selected filters.</div>
    );
  }

  return (
    <div className="py-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">Current Batch Details</p>
          <div className="mt-1 flex items-center gap-2">
            <p className="text-xl font-semibold text-slate-800">{batch.batchId}</p>
            <span className="rounded bg-violet-50 px-2 py-1 text-xs font-semibold text-brand">
              {batch.raw?.total_cycles ?? "—"} / {batch.totalParts ?? "—"}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate(`/setting/batch/${encodeURIComponent(batch.uid)}${location.search}`, { state: { from: `${location.pathname}${location.search}` } })}
          className="inline-flex items-center gap-1 rounded bg-slate-50 px-3 py-2 text-xs font-medium text-slate-500 hover:text-brand"
        >
          View Detail
          <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
        <span><strong className="font-semibold text-slate-500">Last updated:</strong> {relativeUpdatedAt(batch.lastUpdated)}</span>
        {[batch.line, batch.shift, batch.machine, batch.partLabel, batch.operator].filter(Boolean).map((item, index) => (
          <span key={`${item}-${index}`} className="before:mr-3 before:text-slate-300 before:content-['•']">{item}</span>
        ))}
        <BatchStatusBadge status={batch.status} className="ml-auto" />
      </div>
    </div>
  );
};

export default CurrentBatchCard;
